// The live-event protocol (ADR 0005, COLLABORATION-PLAN.md §7).
//
// Events are hints: each tells the app what changed, by id, and the app fetches
// the thing itself over the REST API, which stays the source of truth.

/** One frame from the server. */
export interface RealtimeEnvelope {
  /** Protocol version. A version this code does not know means reload. */
  v: number;
  /** Dotted event type, e.g. `notification.created`. */
  type: string;
  /** Who it is for, e.g. `user:0192…`. Absent on control frames. */
  topic?: string;
  /** Server time, ISO 8601 UTC. */
  ts: string;
  /** Identifiers only — never record contents. */
  data?: Record<string, unknown>;
}

export const PROTOCOL_VERSION = 1;

/**
 * Frames the browser sends (ADR 0005, 0008). Only these: the server accepts
 * no writes over the socket, only signals about this connection.
 */
export type ClientFrame =
  /** The person used the app. At most once a minute. */
  | { type: 'activity' }
  /** Everyone whose presence badge is on screen, at most 500. Replaces the last set. */
  | { type: 'presence.subscribe'; ids: string[] };

/** Close codes the server uses (internal/platform/realtime/hub.go). */
export const CloseCode = {
  /** Server restarting: reconnect, probably to another instance. */
  GoingAway: 1001,
  /** No valid ticket, or the session ended: do not reconnect. */
  Unauthorised: 4401,
  /** This connection fell behind: reconnect and refetch. */
  SlowConsumer: 4408,
} as const;

/** Where the live connection stands, as the UI needs to know it. */
export type RealtimeStatus =
  /** Not started, or deliberately stopped (signed out). */
  | 'idle'
  | 'connecting'
  | 'live'
  /** Trying again after a drop. Data still refreshes by polling meanwhile. */
  | 'reconnecting'
  /** Several attempts in a row never opened — a proxy is probably blocking WebSockets. */
  | 'fallback'
  /** The server said the session is over. */
  | 'ended';

/** Parse one frame, rejecting anything that is not a well-formed envelope. */
export function parseEnvelope(raw: unknown): RealtimeEnvelope | null {
  if (typeof raw !== 'string') return null;
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof value !== 'object' || value === null) return null;
  const env = value as Partial<RealtimeEnvelope>;
  if (typeof env.v !== 'number' || typeof env.type !== 'string' || typeof env.ts !== 'string') {
    return null;
  }
  return env as RealtimeEnvelope;
}
